/**
 * AC-1599 / AC-1594: React Query mutation hooks for the Projects resource.
 *
 * Phase 4 (GREEN) of the AC-1594 project-clone epic. The first export here is
 * `useCloneProject()` — the wiring contract between the AC-1597 backend
 * endpoint (`POST /api/v1/projects/{id}/clone`) and the AC-1599 frontend
 * `CloneProjectModal`.
 *
 * ## Why a NEW file (and not adding to useWorkflowMutations.ts)
 *
 * Projects are their own resource family in the queryKeys factory
 * (`queryKeys.projects()` — AC-1599 just added it as a sibling of
 * `workflows()`). The existing `useWorkflowMutations.ts` is scoped to the
 * workflow resource and its hierarchy; adding project mutations there would
 * blur the resource-per-file convention that AC-1377's React Query migration
 * established. This file gives projects their own home for current AND future
 * mutations (rename, update visibility, etc.).
 *
 * ## Hybrid invalidation pattern (Zustand + React Query)
 *
 * The projects list is currently owned by `useProjectStore` (Zustand). A full
 * React Query migration of the projects list is a separate ticket (see the
 * AC-1377 family). Until then, `useCloneProject().onSuccess` runs BOTH:
 *
 *   1. `queryClient.invalidateQueries({ queryKey: queryKeys.projects() })` —
 *      so any future React Query subscriber of the projects list refreshes
 *      automatically once the migration lands. Adding it now means no caller
 *      needs to be touched at migration time.
 *   2. `useProjectStore.getState().loadProjects()` — so the existing Zustand-
 *      backed `ProjectsPage` (which today holds the source of truth) re-fetches
 *      and the new clone appears in the list without a page reload.
 *
 * Why both: the two caches are independent. Dropping either would leave a
 * stale view somewhere. This mirrors the workflows-list bridging done during
 * AC-1377's incremental migration.
 *
 * On failure (404, 409, 500, ...) NEITHER side is touched — the cache must
 * not be perturbed by a failed write.
 *
 * Ordering: invalidate RQ keys first (synchronous mark-as-stale), then call the
 * Zustand action (async refetch). Reversing this risks the Zustand refetch landing
 * before the RQ key is marked stale, deferring the RQ-side refresh by one event
 * loop tick. This convention applies to any future hybrid RQ + Zustand mutations.
 */
import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from '@tanstack/react-query';

import apiService from '../../services/api';
import { useProjectStore } from '../../store';
import { Project } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * AC-1599: Clone-project mutation input.
 *
 * `projectId` is the source project id; the payload matches the backend's
 * `ProjectCloneRequest` schema: `{ name }`. The hook does NOT coerce the id —
 * URL interpolation is the apiService layer's responsibility. Tests assert
 * that a `'7'` (string) projectId is forwarded verbatim to apiService.
 */
export type CloneProjectVariables = {
  projectId: string | number;
  payload: {
    name: string;
  };
};

/**
 * `useCloneProject()` — mutation hook for AC-1599's "Clone Project" action.
 *
 * On success:
 *   1. Invalidates `queryKeys.projects()` so any future RQ subscriber
 *      refreshes.
 *   2. Calls `useProjectStore.getState().loadProjects()` so the existing
 *      Zustand-backed `ProjectsPage` re-fetches and the new clone shows up.
 *
 * On failure (e.g. 409 name collision, 404 not found, 500): `isError` is set
 * and NEITHER invalidation NOR `loadProjects()` is called — by design (TD
 * Section 5; AC-1599 ticket brief).
 *
 * @example
 *   const clone = useCloneProject();
 *   const created = await clone.mutateAsync({
 *     projectId: source.project_id,
 *     payload: { name: `${source.name} (copy)` },
 *   });
 *   navigate(`/projects/${created.project_id}`);
 */
export function useCloneProject(): UseMutationResult<
  Project,
  Error,
  CloneProjectVariables
> {
  const queryClient = useQueryClient();
  return useMutation<Project, Error, CloneProjectVariables>({
    mutationFn: ({ projectId, payload }) =>
      apiService.cloneProject(projectId, payload),
    onSuccess: () => {
      // (1) RQ cache: invalidate the projects-list key so any future RQ
      // consumer refreshes. Deliberately covers the post-migration case.
      queryClient.invalidateQueries({ queryKey: queryKeys.projects() });
      // (2) RQ cache: invalidate the global workflows list. Project clone
      // creates new Orchestration rows in the target project, and the
      // workflows-by-project view (WorkflowsPage) reads from the global
      // useAllWorkflows() list filtered client-side. Without this
      // invalidation, the cloned project's Workflows page shows "no
      // workflows" until a manual refresh because the stale list does
      // not include the just-cloned orchestrations (AC-1607).
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows() });
      // (3) Zustand bridge: refresh the Zustand-backed projects list that
      // ProjectsPage still owns today. Use `getState()` (not the hook form)
      // because we only need an imperative one-shot call - no subscription.
      void useProjectStore
        .getState()
        .loadProjects()
        .catch((e) => {
          console.error('[useCloneProject] Zustand bridge loadProjects() failed', e);
        });
    },
  });
}
